import { useCallback, useEffect, useRef } from 'react';
import { 
    Edge as FlowEdge, 
    useNodesState, 
    useEdgesState, 
    addEdge, 
    MarkerType,
} from 'reactflow';
import { NodeConnection, NodeExecutionCondition, NodeTestRunInstance, WorkflowNode, WorkflowOutput } from '../../types/workflow';
import { convertToFlowNodes } from './flowUtils';
import getLayoutedElements from './getLayoutedElements';
import { WorkflowIntegration } from '../../types/integrations';
import CustomEdge from './CustomEdge';
import FlowWorkflowNode from './FlowWorkflowNode';
import { NodeConfig } from '../../types/nodes';
import { Node } from '../../types/workflow';

const nodeTypes = {
    workflowNode: FlowWorkflowNode,
};

const edgeTypes = {
    custom: CustomEdge,
};

const useReactFlowManager = (
    workflowNodes: WorkflowNode[], 
    nodeConfigs: any, 
    integrations: WorkflowIntegration[] | undefined,
    setSelectedWorkflowNodeId: (nodeId: string) => void,
    setIsFloatingPanelOpen: (isOpen: boolean) => void,
    handleAddNodeClick: (parentNode?: WorkflowNode) => void,
    nodeConnections: NodeConnection[],
    updateNodeConfigsFromRunInstances: (nodeId: string, response_config: string, nodeConfigs?: Record<string, NodeConfig>) => void,
    setTestRunInstances: React.Dispatch<React.SetStateAction<Record<string, NodeTestRunInstance>>>,
    deleteNodeById: (params: { nodeId: string }) => void,
    shouldDeleteNode: () => void,
    setNodeConfigs: React.Dispatch<React.SetStateAction<Record<string, NodeConfig>>>,
    setNodeResponseConfigs: React.Dispatch<React.SetStateAction<Record<string, Record<string, string>>>>,
    nodeResponseConfigs: Record<string, Record<string, string>>,
    setNodeConfigsForNodeInRef: (nodeId: string, newNodeConfigs: NodeConfig) => void,
    refreshWorkflowNodes: () => void,
    executionConditionsForNodes: Record<string, NodeExecutionCondition[]>,
    getHasChildren: (node: WorkflowNode) => boolean,
    saveNodeConnection?: (params: { workflow: WorkflowOutput; start_node: Node; end_node: Node }, callbacks?: { onSuccess?: (nodeConnection: NodeConnection) => void }) => void,
    currentWorkflow?: WorkflowOutput | null,
    fetchNodeConnections?: () => void,
    handleAddNodeBetween?: (startNodeId: string, endNodeId: string) => void,
) => {
    const [flowNodes, setFlowNodes, onNodesChange] = useNodesState([]);
    const [flowEdges, setFlowEdges, onEdgesChange] = useEdgesState([]);
    
    // Track connection state to distinguish between successful and cancelled connections
    const connectionWasSuccessfulRef = useRef(false);
    const connectionStartInfoRef = useRef<{ nodeId?: string; handleId?: string } | null>(null);

    const handleNodeClick = useCallback((workflowNode: WorkflowNode) => {
        setSelectedWorkflowNodeId(workflowNode.node.id || '');
        setIsFloatingPanelOpen(true);
    }, [setSelectedWorkflowNodeId, setIsFloatingPanelOpen]);

    // Convert workflow nodes to flow nodes and edges
    useEffect(() => {
        if (workflowNodes.length) {
            const nodes = convertToFlowNodes(
                workflowNodes, 
                nodeConfigs, 
                integrations, 
                handleNodeClick,
                (node) => !nodeConnections.some(connection => connection.start_node.id === node.node.id),
                updateNodeConfigsFromRunInstances,
                setTestRunInstances,
                handleAddNodeClick,
                deleteNodeById,
                shouldDeleteNode,
                setNodeConfigs,
                setNodeResponseConfigs,
                setNodeConfigsForNodeInRef,
                refreshWorkflowNodes,
                executionConditionsForNodes,
                getHasChildren,
            );

            // Deduplicate connections: if multiple connections exist between the same two nodes,
            // prefer the one with is_ui_edge = true, otherwise use the earliest created
            const relevantConnections = nodeConnections.reduce((acc, connection) => {
                const key = `${connection.start_node.id}_${connection.end_node.id}`;
                const existingConnection = acc.get(key);
                if (!existingConnection || 
                    // Prioritize UI edges
                    (connection.is_ui_edge && !existingConnection.is_ui_edge) ||
                    // If both or neither are UI edges, use the earliest
                    ((connection.is_ui_edge === existingConnection.is_ui_edge) && 
                     connection.created_time < existingConnection.created_time)) {
                    acc.set(key, connection);
                }
                return acc;
            }, new Map());
            
            // Separate check: filter connections by end node
            // If an end node has a UI edge connection, remove all non-UI edge connections to that end node
            // Keep ALL UI edge connections (do not filter them)
            // If multiple non-UI edge connections exist to the same end node, keep only the oldest one
            // TODO: This is fix to support old workflows where uiedge is not strictly set
            const filteredConnections = new Map();
            const connectionsByEndNode = new Map<string, typeof relevantConnections>();
            
            // Group connections by end node
            relevantConnections.forEach((connection, key) => {
                const endNodeId = connection.end_node.id;
                if (!connectionsByEndNode.has(endNodeId)) {
                    connectionsByEndNode.set(endNodeId, new Map());
                }
                connectionsByEndNode.get(endNodeId)!.set(key, connection);
            });
            
            // Process each end node
            connectionsByEndNode.forEach((connections, endNodeId) => {
                const uiEdges = Array.from(connections.values()).filter(conn => conn.is_ui_edge);
                const nonUIEdges = Array.from(connections.values()).filter(conn => !conn.is_ui_edge);
                
                if (uiEdges.length > 0) {
                    // If UI edges exist, keep ALL UI edges and remove all non-UI edges
                    uiEdges.forEach(uiEdge => {
                        const key = `${uiEdge.start_node.id}_${uiEdge.end_node.id}`;
                        filteredConnections.set(key, uiEdge);
                    });
                } else if (nonUIEdges.length > 0) {
                    // If no UI edges, keep only the oldest non-UI edge
                    const oldestNonUIEdge = nonUIEdges.reduce((oldest, current) => 
                        current.created_time < oldest.created_time ? current : oldest
                    );
                    const oldestKey = `${oldestNonUIEdge.start_node.id}_${oldestNonUIEdge.end_node.id}`;
                    filteredConnections.set(oldestKey, oldestNonUIEdge);
                }
            });

            // Convert relevant connections to flow edges with optional styling
            const edges = Array.from(filteredConnections.values()).map(connection => {
                const isOptional = connection.is_optional || false;
                return {
                    id: `edge_${connection.start_node.id}_${connection.end_node.id}`,
                    source: connection.start_node.id,
                    target: connection.end_node.id,
                    type: 'custom',
                    animated: false,
                    style: {
                        stroke: isOptional ? '#9CA3AF' : '#6366f1',
                        strokeWidth: 2,
                        strokeDasharray: isOptional ? '8 4' : '0',
                    },
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        color: isOptional ? '#9CA3AF' : '#6366f1',
                    },
                    data: {
                        is_optional: isOptional,
                        connectionId: connection.id,
                        onAddNode: handleAddNodeBetween,
                    },
                };
            });

            const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges);
            
            setFlowNodes(layoutedNodes);
            setFlowEdges(layoutedEdges);
        } else {
            setFlowNodes([]);
            setFlowEdges([]);
        }
    }, [workflowNodes, nodeConfigs, integrations, handleNodeClick, nodeConnections, nodeResponseConfigs]);

    // Track when connection starts
    const onConnectStart = useCallback(
        (event: React.MouseEvent | React.TouchEvent, params: any) => {
            connectionWasSuccessfulRef.current = false; // Reset flag
            connectionStartInfoRef.current = {
                nodeId: params.nodeId,
                handleId: params.handleId,
            };
        },
        []
    );

    // Connect nodes
    const onConnect = useCallback(
        (params: any) => {
            connectionWasSuccessfulRef.current = true; // Mark connection as successful
            // Add edge to ReactFlow state immediately for visual feedback
            const newEdge: FlowEdge = { 
                ...params, 
                type: 'custom', 
                animated: true,
                style: { stroke: '#6366f1', strokeWidth: 2 },
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    color: '#6366f1',
                },
                id: `edge_${params.source}_${params.target}_${Date.now()}`
            };
            setFlowEdges((eds) => addEdge(newEdge, eds));

            // Save connection to backend if callback and workflow are available
            if (saveNodeConnection && currentWorkflow) {
                // Find the actual Node objects from workflowNodes
                const startWorkflowNode = workflowNodes.find(wn => wn.node.id === params.source);
                const endWorkflowNode = workflowNodes.find(wn => wn.node.id === params.target);

                if (startWorkflowNode?.node && endWorkflowNode?.node) {
                    console.log("saveNodeConnection", startWorkflowNode.node, endWorkflowNode.node);
                    saveNodeConnection({
                        workflow: currentWorkflow,
                        start_node: startWorkflowNode.node as Node,
                        end_node: endWorkflowNode.node as Node,
                    }, {
                        onSuccess: (nodeConnection) => {
                            // Refresh connections and workflow nodes to sync with backend
                            if (fetchNodeConnections) {
                                // fetchNodeConnections();
                            }
                            refreshWorkflowNodes();
                        }
                    });
                }
            }
        },
        [saveNodeConnection, currentWorkflow, workflowNodes, fetchNodeConnections, refreshWorkflowNodes]
    );

    // Handle connection end (including when connection is cancelled/not connected to a target)
    // Use connectionWasSuccessfulRef to determine if connection was successful or cancelled
    const onConnectEnd = useCallback(
        (event: MouseEvent | TouchEvent) => {
            if (!connectionWasSuccessfulRef.current) {
                // Connection was cancelled/dropped without connecting to a target node
                const startNodeId = connectionStartInfoRef.current?.nodeId;
                
                if (startNodeId) {
                    // Find the workflow node that was the source of the cancelled connection
                    const startWorkflowNode = workflowNodes.find(wn => wn.node.id === startNodeId);
                    
                    if (startWorkflowNode) {
                        // Open the create dialog with the start node as the parent
                        // This will create a new node and connect it to the start node
                        handleAddNodeClick(startWorkflowNode);
                    }
                }
            }
            // Reset for next connection attempt
            connectionWasSuccessfulRef.current = false;
            connectionStartInfoRef.current = null;
        },
        [workflowNodes, handleAddNodeClick]
    );

    return {
        flowNodes,
        flowEdges,
        onNodesChange,
        onEdgesChange,
        onConnect,
        onConnectStart,
        onConnectEnd,
        nodeTypes,
        edgeTypes,
    };
};

export default useReactFlowManager; 